/**
 * Sections - Shopify Section Handler with Lazy Loading Support
 * Migrated from Slate to native ES modules
 */
export default class Sections {
  constructor() {
    this.handlers = {};
    this.lazyHandlers = {};  // For dynamic import handlers
    this.instances = {};
    this._onSectionEvent = this._onSectionEvent.bind(this);

    document.addEventListener('shopify:section:load', this._onSectionEvent);
    document.addEventListener('shopify:section:unload', this._onSectionEvent);
    document.addEventListener('shopify:section:select', this._onSectionEvent);
    document.addEventListener('shopify:section:deselect', this._onSectionEvent);
    document.addEventListener('shopify:block:select', this._onSectionEvent);
    document.addEventListener('shopify:block:deselect', this._onSectionEvent);
  }

  /**
   * Stop listening for section events, and unbind all handlers.
   */
  unbind() {
    document.removeEventListener('shopify:section:load', this._onSectionEvent);
    document.removeEventListener('shopify:section:unload', this._onSectionEvent);
    document.removeEventListener('shopify:section:select', this._onSectionEvent);
    document.removeEventListener('shopify:section:deselect', this._onSectionEvent);
    document.removeEventListener('shopify:block:select', this._onSectionEvent);
    document.removeEventListener('shopify:block:deselect', this._onSectionEvent);

    // Unload all instances
    Object.values(this.instances).forEach(instance => {
      this._triggerInstanceEvent(instance, 'onSectionUnload');
    });

    this.handlers = {};
    this.lazyHandlers = {};
    this.instances = {};
  }

  /**
   * Register a section handler (immediate).
   *
   * @param {string} type - The section type to handle
   * @param {function} handler - The handler function
   */
  register(type, handler) {
    if (this.handlers[type]) {
      console.warn(`Sections: section handler already exists of type '${type}'.`);
    }

    // Store the section handler
    this.handlers[type] = handler;

    // Init sections for this type
    this._initSections(type);
  }

  /**
   * Register a lazy-loaded section handler.
   * The loader function will only be called when the section is needed.
   *
   * @param {string} type - The section type to handle
   * @param {function} loader - Async function that returns the module (e.g., () => import('./section-foo'))
   */
  registerLazy(type, loader) {
    if (this.handlers[type] || this.lazyHandlers[type]) {
      console.warn(`Sections: section handler already exists of type '${type}'.`);
      return;
    }

    this.lazyHandlers[type] = loader;

    // Check if section exists on page and load immediately if so
    const existsOnPage = document.querySelector(`[data-section-type="${type}"]`);
    if (existsOnPage) {
      this._loadAndRegister(type);
    }
  }

  /**
   * Load a lazy handler and convert it to a regular handler
   */
  async _loadAndRegister(type) {
    const loader = this.lazyHandlers[type];
    if (!loader) return;

    try {
      const module = await loader();
      const SectionClass = module.default;

      // Convert to regular handler
      this.handlers[type] = (section) => new SectionClass(section);
      delete this.lazyHandlers[type];

      // Init any existing sections of this type
      this._initSections(type);
    } catch (error) {
      console.error(`Sections: failed to load section handler for '${type}'.`, error);
    }
  }

  /**
   * Initialize sections already on the page.
   */
  _initSections(type) {
    const dataEls = document.querySelectorAll(`[data-section-type="${type}"]`);
    if (!dataEls || !dataEls.length) return;

    for (let i = 0; i < dataEls.length; i++) {
      const dataEl = dataEls[i];
      const el = dataEl.parentNode;

      const idEl = el.querySelector('[data-section-id]');
      if (!idEl) {
        console.warn(`Sections: unable to find section id for '${type}'.`, el);
        continue;
      }

      const sectionId = idEl.getAttribute('data-section-id');
      if (!sectionId) {
        console.warn(`Sections: unable to find section id for '${type}'.`, el);
        continue;
      }

      this._createInstance(sectionId, el);
    }
  }

  async _onSectionEvent(event) {
    const el = event.target;
    const sectionId = event.detail.sectionId;
    const blockId = event.detail.blockId;
    const instance = this.instances[sectionId];

    switch (event.type) {
      case 'shopify:section:load':
        await this._createInstance(sectionId, el);
        break;

      case 'shopify:section:unload':
        this._triggerInstanceEvent(instance, 'onSectionUnload', { el, id: sectionId });
        delete this.instances[sectionId];
        break;

      case 'shopify:section:select':
        this._triggerInstanceEvent(instance, 'onSectionSelect', { el, id: sectionId });
        break;

      case 'shopify:section:deselect':
        this._triggerInstanceEvent(instance, 'onSectionDeselect', { el, id: sectionId });
        break;

      case 'shopify:block:select':
        this._triggerInstanceEvent(instance, 'onSectionBlockSelect', { el, id: blockId });
        break;

      case 'shopify:block:deselect':
        this._triggerInstanceEvent(instance, 'onSectionBlockDeselect', { el, id: blockId });
        break;
    }
  }

  _triggerInstanceEvent(instance, eventName, ...args) {
    if (instance && instance[eventName]) {
      instance[eventName](...args);
    }
  }

  _postMessage(name, data) {
    Object.keys(this.instances).forEach((id) => {
      this._triggerInstanceEvent(this.instances[id], 'onSectionMessage', name, data);
    });
  }

  async _createInstance(id, el) {
    const typeEl = el.querySelector('[data-section-type]');
    if (!typeEl) return;

    const type = typeEl.getAttribute('data-section-type');
    if (!type) return;

    // Check for lazy handler first and load if needed
    if (!this.handlers[type] && this.lazyHandlers[type]) {
      await this._loadAndRegister(type);
    }

    const handler = this.handlers[type];
    if (!handler) {
      console.warn(`Sections: unable to find section handler for type '${type}'.`);
      return;
    }

    const data = this._loadData(el);
    const postMessage = this._postMessage.bind(this);

    this.instances[id] = handler({ id, type, el, data, postMessage });
  }

  _loadData(el) {
    const dataEl = el.querySelector('[data-section-data]');
    if (!dataEl) return {};

    const data = dataEl.getAttribute('data-section-data') || dataEl.innerHTML;

    try {
      return JSON.parse(data);
    } catch (error) {
      console.warn(`Sections: invalid section data found. ${error.message}`);
      return {};
    }
  }
}
